// PRD.312 — Medical History clinic settings + longitudinal history selection.
//
// SETTINGS STORAGE
// ----------------
// The two clinic/org-scoped capability switches live under
// `app_config.extra_json.medical_history`, mirroring the established
// `regional_settings` / `clinic_classification` / `main_modules` /
// `print_branding` / `queue_workflow` groups. That path rides the existing
// PowerSync + SyncWrite `extra_json` plumbing, so PRD.312 needs no schema,
// migration, codegen or sync-rule change. `patient_workspace_settings` is a
// column-per-setting table with no JSON escape hatch, so putting these there
// would have meant two more columns and another migrate + codegen + sync-rules
// deploy for two booleans.
//
// These are CAPABILITY settings, not section visibility. Whether the Medical
// History section appears at all remains the user's Visit Note preference
// (PRD.300) on top of the Guided preset default (PRD.310); these only decide
// what a section that IS visible offers.
//
// This module is intentionally self-contained (no workspace-package imports,
// no PowerSync types) so it runs under the node:test harness.

// ─── Settings ───

/** Clinic/org-scoped Medical History capabilities (PRD.312 §9). */
export interface MedicalHistorySettings {
  /** Render the read-only longitudinal Previous History panel. */
  showPreviousHistory: boolean;
  /** Expose PRD.310's voice recording controls on the current-visit editor. */
  voiceToTextEnabled: boolean;
}

/**
 * Effective defaults for a clinic that has never saved PRD.312 settings.
 *
 * Both ON (PRD §9.3). `voiceToTextEnabled: true` is the backward-compatible
 * choice specifically: PRD.310 shipped voice unconditionally, so defaulting it
 * off would silently remove a capability from every existing clinic.
 */
export const MEDICAL_HISTORY_SETTINGS_DEFAULTS: MedicalHistorySettings = {
  showPreviousHistory: true,
  voiceToTextEnabled: true,
};

/** Minimal structural shape of the app_config row this module reads. */
export interface MedicalHistorySettingsSource {
  extra_json?: string | null;
}

function parseExtraJson(raw: unknown): Record<string, unknown> {
  if (typeof raw !== 'string' || !raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Malformed blob — treat as "nothing saved" rather than throwing.
  }
  return {};
}

function asBool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/**
 * PRD.314 — Specialty History (Dental, Derma, …) carries the SAME two
 * capabilities, but in its own `specialty_history` group so the Medical History
 * switches keep meaning exactly what they say. An admin can leave dictation on
 * for Medical History while turning it off for specialty narratives, or the
 * reverse.
 *
 * One group covers every specialty type rather than one group per type: these
 * are capability switches with no clinical reason to differ between dental and
 * derma, and a card per future specialty would not scale.
 */
export type SpecialtyHistorySettings = MedicalHistorySettings;

export const SPECIALTY_HISTORY_SETTINGS_DEFAULTS: SpecialtyHistorySettings = {
  showPreviousHistory: true,
  voiceToTextEnabled: true,
};

/** extra_json group names. Storage identity — never a display label. */
const MEDICAL_HISTORY_GROUP = 'medical_history';
const SPECIALTY_HISTORY_GROUP = 'specialty_history';

/**
 * Read one history-settings group from an app_config row, falling back
 * field-by-field to its defaults. Always returns a fully-populated object, so a
 * clinic with no stored settings — or a partially-written blob — keeps the full
 * experience rather than losing a capability.
 */
function readHistorySettings(
  row: MedicalHistorySettingsSource | null | undefined,
  group: string,
  defaults: MedicalHistorySettings,
): MedicalHistorySettings {
  const raw = parseExtraJson(row?.extra_json)[group];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...defaults };
  const o = raw as Record<string, unknown>;
  return {
    showPreviousHistory: asBool(o.showPreviousHistory, defaults.showPreviousHistory),
    voiceToTextEnabled: asBool(o.voiceToTextEnabled, defaults.voiceToTextEnabled),
  };
}

/**
 * Serialize one history-settings group into an updated `extra_json` string,
 * preserving every other top-level key (regional_settings,
 * clinic_classification, main_modules, print_branding, queue_workflow, …) —
 * including the OTHER history group.
 */
function writeHistorySettings(
  currentExtraJson: unknown,
  group: string,
  config: MedicalHistorySettings,
): string {
  const next = { ...parseExtraJson(currentExtraJson) };
  next[group] = {
    showPreviousHistory: config.showPreviousHistory,
    voiceToTextEnabled: config.voiceToTextEnabled,
  };
  return JSON.stringify(next);
}

/** PRD.312 — capabilities for the Visit Note Medical History section. */
export function getMedicalHistorySettings(
  row: MedicalHistorySettingsSource | null | undefined,
): MedicalHistorySettings {
  return readHistorySettings(row, MEDICAL_HISTORY_GROUP, MEDICAL_HISTORY_SETTINGS_DEFAULTS);
}

export function writeMedicalHistorySettings(
  currentExtraJson: unknown,
  config: MedicalHistorySettings,
): string {
  return writeHistorySettings(currentExtraJson, MEDICAL_HISTORY_GROUP, config);
}

/** PRD.314 — capabilities for every Specialty History section (dental, derma). */
export function getSpecialtyHistorySettings(
  row: MedicalHistorySettingsSource | null | undefined,
): SpecialtyHistorySettings {
  return readHistorySettings(row, SPECIALTY_HISTORY_GROUP, SPECIALTY_HISTORY_SETTINGS_DEFAULTS);
}

export function writeSpecialtyHistorySettings(
  currentExtraJson: unknown,
  config: SpecialtyHistorySettings,
): string {
  return writeHistorySettings(currentExtraJson, SPECIALTY_HISTORY_GROUP, config);
}

// ─── Longitudinal history selection ───

/** One prior encounter's Medical History, as rendered in Previous History. */
export interface PriorMedicalHistoryEntry {
  encounterId: string;
  /** Best available visit date ('YYYY-MM-DD' or ISO). Null when unresolvable. */
  visitDate: string | null;
  /** The prior encounter's narrative. Never empty — blanks are filtered out. */
  text: string;
}

/** The subset of an encounter row this selection needs. */
export interface PriorHistorySourceEncounter {
  id: string;
  medical_history?: string | null;
  /**
   * Chronology key. `started_at` is a full timestamp and is always populated,
   * so it orders same-day encounters correctly where the resolved visit date
   * (often date-only) cannot. Display still uses the resolved visit date.
   */
  started_at?: string | null;
  created_at?: string | null;
}

/**
 * The value used to order encounters against each other. Deliberately NOT the
 * resolved visit date: that is display-oriented and frequently date-only, so
 * two encounters on the same day would compare equal.
 */
function chronologyKey(
  e: PriorHistorySourceEncounter,
  visitDateByEncounter: ReadonlyMap<string, string | null>,
): string | null {
  return e.started_at ?? e.created_at ?? visitDateByEncounter.get(e.id) ?? null;
}

/**
 * Build the read-only Previous Visits list for the Medical History panel.
 *
 * Rules (PRD §7.4 / §11.2):
 *  - the ACTIVE encounter is excluded — today's note is the left column, never
 *    part of its own history;
 *  - encounters that happened AFTER the active one are excluded too. "Previous
 *    Visits" means visits before this one: opening an older encounter must not
 *    show later visits as its history, which would misrepresent what was known
 *    at the time it was documented;
 *  - encounters with no Medical History are dropped, so the surface shows
 *    visits that actually documented something rather than a wall of empty
 *    dates;
 *  - OLDEST FIRST (PRD.313 §5.2). The longitudinal surface reads as one
 *    continuing document with the current encounter appended at the bottom, so
 *    history has to run forwards in time towards it.
 *
 * Ordering and the before/after test both use `chronologyKey` (the encounter's
 * own timestamp), while DISPLAY uses `visitDateByEncounter` — the app's
 * canonical resolver (`useVisitDateByEncounter`: visit.visit_date →
 * visit.started_at → encounter.started_at → encounter.created_at). No second
 * date mapping is introduced.
 *
 * When the active encounter's own key cannot be resolved, no chronological
 * filter is applied: showing every other documented visit is more useful than
 * showing none, and the entries stay clearly dated.
 *
 * Pure: this only SELECTS existing rows for display. It never writes, merges or
 * copies history into the active encounter.
 */
export function selectPriorMedicalHistory(
  encounters: readonly PriorHistorySourceEncounter[],
  activeEncounterId: string,
  visitDateByEncounter: ReadonlyMap<string, string | null>,
): PriorMedicalHistoryEntry[] {
  const active = encounters.find((e) => e.id === activeEncounterId);
  const activeKey = active ? chronologyKey(active, visitDateByEncounter) : null;

  const entries: { key: string | null; entry: PriorMedicalHistoryEntry }[] = [];
  for (const e of encounters) {
    if (!e.id || e.id === activeEncounterId) continue;
    const text = (e.medical_history ?? '').trim();
    if (!text) continue;
    const key = chronologyKey(e, visitDateByEncounter);
    // Strictly earlier than the active encounter. An entry with no key at all
    // is kept only when there is nothing to compare it against.
    if (activeKey) {
      if (!key || key >= activeKey) continue;
    }
    entries.push({
      key,
      entry: { encounterId: e.id, visitDate: visitDateByEncounter.get(e.id) ?? null, text },
    });
  }

  // Oldest first; keyless entries sink to the bottom, nearest the current
  // entry, since an undated note is most likely recent. ISO timestamps and
  // 'YYYY-MM-DD' both sort correctly as strings.
  entries.sort((a, b) => {
    if (!a.key && !b.key) return 0;
    if (!a.key) return 1;
    if (!b.key) return -1;
    return a.key.localeCompare(b.key);
  });
  return entries.map((e) => e.entry);
}

/** Normalize the patient-record Medical History for read-only display. */
export function selectPatientMedicalHistory(
  patient: { medical_history?: string | null } | null | undefined,
): string | null {
  const text = (patient?.medical_history ?? '').trim();
  return text ? text : null;
}
